import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { usePreviousValue } from '@wix/da-hooks/pkg/usePreviousValue';
import { AppState } from '../types/store';
import { getModuleRequestsPath } from '../redux/selectors/modules';
import { fetchModuleEndpoint, fetchModuleEndpointPurgeData } from '../redux/actions/modules';
import { useGruser } from './useGruser';
import usePage from './usePage';

export function useModuleEndpoint(moduleId: number, path: string = '/') {
  const dispatch = useDispatch();
  const { gruser } = useGruser();
  const page = usePage();

  const {
    fetchedPath,
    response,
    isFetching,
    errorDescription: error,
  } = (useSelector((state: AppState) => getModuleRequestsPath(state, gruser, moduleId, path)) ||
    {}) as any;

  const wasFetching = usePreviousValue(isFetching);
  const hasJustFinishedFetching = wasFetching && !isFetching;
  const moduleVersion = response?.config?.version;

  const reset = useCallback(() => {
    dispatch(
      fetchModuleEndpointPurgeData(gruser!, {
        id: moduleId,
        path,
      })
    );
  }, [dispatch, gruser, moduleId, path]);

  const fetchEndpoint = useCallback(
    ({ data, method = 'get' }) => {
      if (!isFetching) {
        dispatch(
          fetchModuleEndpoint(gruser!, {
            ...data,
            id: moduleId,
            pagename: page?.name,
            path,
            method,
            version: moduleVersion,
          })
        );
      }
    },
    [isFetching, moduleVersion, dispatch, gruser, page, moduleId, path]
  );

  const getEndpoint = useCallback(
    (data = {}) => fetchEndpoint({ data, method: 'get' }),
    [fetchEndpoint]
  );

  const postEndpoint = useCallback(
    (data = {}) => fetchEndpoint({ data, method: 'post' }),
    [fetchEndpoint]
  );

  return {
    getEndpoint,
    postEndpoint,
    fetchEndpoint,
    isFetching,
    hasJustFinishedFetching,
    fetchedPath,
    response,
    error,
    reset,
  };
}

export default useModuleEndpoint;
